/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useCallback } from 'react';
import { buildUrl, stripPathPrefix } from '@salesforce/storefront-next-runtime/site-context';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useCurrentSiteAndLocaleRef } from '@/hooks/use-current-site-and-locale-ref';
import { appendSuffix, findLegacyRoute, matchesRoutePattern } from '@/middlewares/legacy-routes';
import { useNavigate } from '@/hooks/use-navigate';
import { routes } from '@/route-paths';

/** All page-route patterns owned by Storefront Next, used to distinguish them from SFRA paths. */
const SFNEXT_ROUTE_PATTERNS = Object.values(routes);

/**
 * Returns a navigate function that is aware of hybrid legacy routes.
 *
 * Two checks run in sequence when hybrid mode is enabled:
 *
 * 1. **Explicit `legacyRoutes` patterns.** If the path matches a configured pattern the
 *    request is handed off via `window.location.href`, with any configured suffix (e.g. `.html`)
 *    appended. This handles known SFRA destinations like `/cart` or `/checkout`.
 *
 * 2. **Unknown-path fallback.** After stripping the site/locale prefix, if the bare path does
 *    not match any registered Storefront Next route it is also handed off as a full-page
 *    navigation. This catches arbitrary SFRA content pages (e.g. `/holiday-gift-guide.html`)
 *    that have no predefined `legacyRoutes` entry.
 *
 * Use this wherever navigation is triggered programmatically from raw HTML content — for example,
 * delegated click handlers on Page Designer markup — where links cannot go through the `<Link>`
 * component's `reloadDocument` logic.
 */
export function useLegacyAwareNavigate(): (path: string) => void {
    const navigate = useNavigate();
    const config = useConfig();
    const { siteRef, localeRef } = useCurrentSiteAndLocaleRef();

    return useCallback(
        (path: string) => {
            const hybrid = config.hybrid;
            if (hybrid?.enabled) {
                // Split query string / hash off the path so matching and suffixing operate on
                // the path alone — same normalization used in the <Link> component.
                const queryHashStart = path.search(/[?#]/);
                const pathPart = queryHashStart === -1 ? path : path.slice(0, queryHashStart);
                const queryHash = queryHashStart === -1 ? '' : path.slice(queryHashStart);

                // 1. Explicit legacy-route check: opt-in patterns with optional suffix support.
                if (hybrid.legacyRoutes?.length) {
                    const matched = findLegacyRoute(pathPart, hybrid.legacyRoutes);
                    if (matched) {
                        const suffixedPath = `${appendSuffix(pathPart, matched.suffix)}${queryHash}`;
                        window.location.href = buildUrl({
                            to: suffixedPath,
                            urlConfig: config.url,
                            params: { siteId: siteRef, localeId: localeRef },
                        });
                        return;
                    }
                }

                // 2. Unknown-path fallback: if the bare path (after stripping any site/locale
                //    prefix) does not match any registered Storefront Next route, treat it as a
                //    legacy route. This catches arbitrary SFRA content pages like
                //    `/holiday-gift-guide.html` that can't be enumerated in legacyRoutes.
                const strippedPath =
                    stripPathPrefix({ pathname: pathPart, prefix: config.url?.prefix ?? '' }) || '/';
                const isSfnextRoute = SFNEXT_ROUTE_PATTERNS.some((pattern) =>
                    matchesRoutePattern(strippedPath, pattern)
                );
                if (!isSfnextRoute) {
                    window.location.href = buildUrl({
                        to: `${pathPart}${queryHash}`,
                        urlConfig: config.url,
                        params: { siteId: siteRef, localeId: localeRef },
                    });
                    return;
                }
            }

            void navigate(path);
        },
        [navigate, config, siteRef, localeRef]
    );
}
